import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import {
  Article,
  UserInfo,
  PersistentNote,
  EmbeddedAnalysisResult,
} from "@/types/canonical";
import { DetailedSuggestionSchema } from "@/types/report";
import {
  diff_match_patch,
  DIFF_DELETE,
  DIFF_INSERT,
  DIFF_EQUAL,
} from "diff-match-patch";

// Helper function to construct the full avatar URL (copied from ArticleDetailPage)
const constructAvatarUrl = (
  apiBaseUrl: string,
  relativePath?: string | null,
): string | undefined => {
  if (!relativePath) return undefined;
  if (relativePath.match(/^https?:\/\//)) {
    return relativePath;
  }
  // Derive the base URL for static assets by removing /api suffix and trailing slash
  const staticBaseUrl = apiBaseUrl.replace(/(\/api)?\/?$/, "");

  // Ensure no double slashes if relativePath starts with /
  const cleanRelativePath = relativePath.startsWith("/")
    ? relativePath.substring(1)
    : relativePath;
  return `${staticBaseUrl}/${cleanRelativePath}`;
};

// Helper function to get user display name (copied from ArticleDetailPage)
const getUserDisplayName = (userInfo?: UserInfo | null): string => {
  if (!userInfo) return "Unknown User";
  return userInfo.name || userInfo.username;
};

// Helper function to get avatar fallback (copied from ArticleDetailPage)
const getAvatarFallback = (userInfo?: UserInfo | null): string => {
  if (!userInfo) return "UU";
  const name = userInfo.name;
  if (name) {
    const parts = name.split(" ");
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  return userInfo.username?.substring(0, 2).toUpperCase() ?? "??";
};

interface AnalysisEntryDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  entry: EmbeddedAnalysisResult & { comments?: PersistentNote[] };
  articleId: string;
  apiBaseUrl: string;
  fetchData: (showLoading?: boolean) => Promise<void>; // To refresh data in parent
  // Removed onUpdateArticle and onUpdateAnalysisDetails, will use fetchData
}

// --- DetailedSuggestionDiff Component (Moved from ArticleDetailPage) ---
const DetailedSuggestionDiff = React.memo(
  ({
    suggestion,
    articleId,
    apiBaseUrl,
    fetchData, // Added fetchData prop
  }: {
    suggestion: DetailedSuggestionSchema;
    articleId: string;
    apiBaseUrl: string;
    fetchData: (showLoading?: boolean) => Promise<void>; // Define the prop type
  }) => {
    const { token } = useAuth();
    const [status, setStatus] = useState<string | null>(
      suggestion.user_status || null,
    );
    const [reason, setReason] = useState<string>(
      suggestion.user_reason || "",
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showReasonInput, setShowReasonInput] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const propStatus = suggestion.user_status || null;
      const propReason = suggestion.user_reason || "";
      setStatus(propStatus);
      setReason(propReason);
      if (propStatus) {
        setShowReasonInput(false);
        setError(null);
      }
    }, [suggestion.user_status, suggestion.user_reason]);

    const handleAcceptSuggestion = async () => {
      if (
        !articleId ||
        !token ||
        !suggestion.original_section ||
        !suggestion.updated_section
      ) {
        setError(
          "Missing required information (article ID, token, or suggestion content).",
        );
        return;
      }
      setIsSubmitting(true);
      setError(null);
      try {
        const payload = {
          original_section: suggestion.original_section,
          updated_section: suggestion.updated_section,
        };
        await axios.post(
          `${apiBaseUrl}/canonical/articles/${articleId}/suggestions/accept`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setStatus("accepted");
        await fetchData(); // Use fetchData prop
      } catch (err: any) {
        console.error("Error accepting suggestion:", err);
        const errorMsg =
          axios.isAxiosError(err) && err.response
            ? err.response.data?.detail || err.message
            : err instanceof Error
              ? err.message
              : "Failed to accept suggestion.";
        setError(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleDeclineSuggestion = async () => {
      if (
        !articleId ||
        !token ||
        !suggestion.original_section ||
        !suggestion.updated_section
      ) {
        setError(
          "Missing required information (article ID, token, or suggestion content).",
        );
        return;
      }
      if (!showReasonInput) {
        setShowReasonInput(true);
        setError(null);
        setReason("");
        return;
      }
      if (showReasonInput && !reason.trim()) {
        setError("Please provide a reason for declining this suggestion.");
        return;
      }
      setIsSubmitting(true);
      setError(null);
      try {
        const payload = {
          original_section: suggestion.original_section,
          updated_section: suggestion.updated_section,
          status_reason: reason.trim(),
        };
        await axios.post(
          `${apiBaseUrl}/canonical/articles/${articleId}/suggestions/decline`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setStatus("declined");
        setShowReasonInput(false);
        await fetchData(); // Use fetchData prop
      } catch (err: any) {
        console.error("Error declining suggestion:", err);
        const errorMsg =
          axios.isAxiosError(err) && err.response
            ? err.response.data?.detail || err.message
            : err instanceof Error
              ? err.message
              : "Failed to decline suggestion.";
        setError(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    };

    const diffs = useMemo(() => {
      const dmp = new diff_match_patch();
      const computedDiffs = dmp.diff_main(
        suggestion.original_section || "",
        suggestion.updated_section || "",
      );
      dmp.diff_cleanupSemantic(computedDiffs);
      return computedDiffs;
    }, [suggestion.original_section, suggestion.updated_section]);

    return (
      <div className="mb-2 w-full rounded border-1 border-base-300 bg-base-200/50 p-2 text-sm">
        <div className="mb-2 overflow-x-auto p-2 font-mono text-xs whitespace-pre-wrap">
          <code>
            {diffs.map(([op, text], index) => {
              let className = "";
              if (op === DIFF_INSERT) {
                className = "bg-success text-success-content";
              } else if (op === DIFF_DELETE) {
                className = "bg-error text-error-content line-through";
              }
              if (
                op === DIFF_EQUAL &&
                text.trim() === "" &&
                !text.includes("\\n")
              ) {
                return null;
              }
              return (
                <span key={index} className={className}>
                  {text}
                </span>
              );
            })}
          </code>
        </div>
        <div className="mt-2 flex flex-col gap-2">
          {error && <div className="mb-1 text-xs text-error">{error}</div>}
          {showReasonInput && (
            <div>
              <textarea
                className="textarea w-full border-none bg-transparent textarea-sm focus:outline-1"
                placeholder="Why are you declining this suggestion? (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            {status ? (
              <div className="tooltip tooltip-top" data-tip="">
                <div
                  className={`badge font-mono text-xs shadow-md select-none ${
                    status === "accepted" ? "badge-info" : "badge-accent"
                  }`}
                >
                  {status === "accepted" ? "Accepted" : "Declined"}
                  {status === "declined" && reason ? `: ${reason}` : ""}
                </div>
                {(suggestion.user_status_updated_by?.name ||
                  suggestion.user_status_updated_at) && (
                  <div className="tooltip-content rounded border border-base-300 bg-base-100 p-2 text-xs text-base-content shadow-lg">
                    {suggestion.user_status_updated_by?.name && (
                      <div>{suggestion.user_status_updated_by.name}</div>
                    )}
                    {suggestion.user_status_updated_at && (
                      <div>
                        {new Date(
                          suggestion.user_status_updated_at,
                        ).toLocaleString("sv-SE")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {showReasonInput ? (
                  <>
                    <button
                      className="btn btn-xs btn-warning"
                      onClick={handleDeclineSuggestion}
                      disabled={isSubmitting || !reason.trim()}
                    >
                      {isSubmitting ? (
                        <span className="loading loading-xs loading-spinner"></span>
                      ) : (
                        "Confirm Decline"
                      )}
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => {
                        setShowReasonInput(false);
                        setReason(suggestion.user_reason || "");
                        setError(null);
                      }}
                      disabled={isSubmitting}
                    >
                      <span>Cancel</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-xs btn-success"
                      onClick={handleAcceptSuggestion}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="loading loading-xs loading-spinner"></span>
                      ) : (
                        "Accept"
                      )}
                    </button>
                    <button
                      className="btn btn-xs btn-warning"
                      onClick={handleDeclineSuggestion}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="loading loading-xs loading-spinner"></span>
                      ) : (
                        "Decline"
                      )}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);

const AnalysisEntryDetailDrawer: React.FC<AnalysisEntryDetailDrawerProps> = ({
  isOpen,
  onClose,
  entry: selectedEntry, // Renaming for clarity within this component
  articleId,
  apiBaseUrl,
  fetchData,
}) => {
  const { token } = useAuth();

  // States for release item comments
  const [releaseItemCommentInput, setReleaseItemCommentInput] = useState("");
  const [isSubmittingReleaseItemComment, setIsSubmittingReleaseItemComment] =
    useState(false);
  const [releaseItemCommentError, setReleaseItemCommentError] = useState<string | null>(null);


  // States for general resolution
  const [generalDeclineReason, setGeneralDeclineReason] = useState("");
  const [showGeneralDeclineReasonInput, setShowGeneralDeclineReasonInput] =
    useState(false);
  const [isSubmittingGeneralResolution, setIsSubmittingGeneralResolution] =
    useState(false);
  const [generalResolutionError, setGeneralResolutionError] = useState<
    string | null
  >(null);

  // Reset local states when the selected entry changes or drawer closes
  useEffect(() => {
    if (!isOpen || !selectedEntry) {
      setReleaseItemCommentInput("");
      setIsSubmittingReleaseItemComment(false);
      setReleaseItemCommentError(null);
      setGeneralDeclineReason("");
      setShowGeneralDeclineReasonInput(false);
      setIsSubmittingGeneralResolution(false);
      setGeneralResolutionError(null);
    }
  }, [isOpen, selectedEntry]);


  const handleReleaseItemCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setReleaseItemCommentInput(e.target.value);
    },
    [],
  );

  const handleAddReleaseItemComment = async (
    releaseNoteId: string | null | undefined,
    releaseItemTitle: string,
  ) => {
    if (!releaseItemCommentInput.trim() || !releaseNoteId || !token || !articleId)
      return;

    setIsSubmittingReleaseItemComment(true);
    setReleaseItemCommentError(null);
    try {
      const payload = {
        text: releaseItemCommentInput.trim(), // Use direct state
        release_note_id: releaseNoteId,
        release_item_title: releaseItemTitle,
      };
      await axios.post<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/notes`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      
      await fetchData(false); // Refresh parent data
      setReleaseItemCommentInput(""); // Clear input
    } catch (err: any) {
      console.error("Error adding release item comment:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to add comment.";
      setReleaseItemCommentError(errorMsg);
    } finally {
      setIsSubmittingReleaseItemComment(false);
    }
  };

  const handleResolveGeneral = async () => {
    if (!selectedEntry || !articleId || !token) return;
    setIsSubmittingGeneralResolution(true);
    setGeneralResolutionError(null);

    try {
      await axios.post<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/analysis-results/${selectedEntry.item_id}/resolve-general`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchData(false); // Refresh parent data
      // Local state for decline reason etc. will be reset by useEffect on selectedEntry change or drawer close
    } catch (err: any) {
      console.error("Error resolving general item:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to resolve item.";
      setGeneralResolutionError(errorMsg);
    } finally {
      setIsSubmittingGeneralResolution(false);
    }
  };

  const handleDeclineGeneral = async () => {
    if (!selectedEntry || !articleId || !token) return;

    if (!showGeneralDeclineReasonInput) {
      setShowGeneralDeclineReasonInput(true);
      setGeneralResolutionError(null);
      return;
    }

    if (!generalDeclineReason.trim()) {
      setGeneralResolutionError(
        "Please provide a reason for declining/dismissing.",
      );
      return;
    }

    setIsSubmittingGeneralResolution(true);
    setGeneralResolutionError(null);

    try {
      const payload = { status_reason: generalDeclineReason.trim() };
      await axios.post<Article>(
        `${apiBaseUrl}/canonical/articles/${articleId}/analysis-results/${selectedEntry.item_id}/decline-general`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchData(false); // Refresh parent data
      
      // Reset decline state on success (or rely on useEffect for selectedEntry change)
      setShowGeneralDeclineReasonInput(false);
      setGeneralDeclineReason("");
    } catch (err: any) {
      console.error("Error declining general item:", err);
      const errorMsg =
        axios.isAxiosError(err) && err.response
          ? err.response.data?.detail || err.message
          : err instanceof Error
            ? err.message
            : "Failed to decline item.";
      setGeneralResolutionError(errorMsg);
    } finally {
      setIsSubmittingGeneralResolution(false);
    }
  };


  if (!isOpen || !selectedEntry) {
    return null; // Or some minimal wrapper if DaisyUI requires it for transitions
  }

  return (
    <div className="drawer drawer-end">
      <input
        id="analysis-details-drawer" // This ID might conflict if multiple drawers use it. Consider making it unique or passed as prop.
        type="checkbox"
        className="drawer-toggle"
        checked={isOpen}
        onChange={(e) => { if (!e.target.checked) onClose(); }} // Call onClose when unchecked
        readOnly // Since we control it with isOpen prop
      />
       {/* Content is rendered if isOpen, drawer-side handles visibility based on checked state */}
      <div className={`drawer-side z-50 ${isOpen ? '' : 'pointer-events-none'}`}>
        <label
          htmlFor="analysis-details-drawer"
          aria-label="close sidebar"
          className="drawer-overlay"
          onClick={onClose}
        ></label>
        <div className="h-full w-full max-w-[80vw] overflow-y-auto bg-base-100 p-4 py-8">
          <div className="space-y-4">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="bg-gradient-to-r from-base-content/80 to-base-content bg-clip-text text-xl font-bold text-transparent">
                {selectedEntry.release_item_title}
              </h2>
              <button
                className="btn btn-circle btn-ghost btn-sm"
                onClick={onClose}
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8"> {/* Adjusted grid for typical 3-col or 4-col layouts */}
              {/* Left Column - Analysis Details */}
              <div className="md:col-span-2 lg:col-span-3 space-y-6"> {/* Main content takes more space */}
                <div className="text-sm text-base-content">
                  <Link
                    className="link link-hover"
                    to={`/release-notes/${selectedEntry.release_note_id}`}
                  >
                    Version: {selectedEntry.release_note_version}
                  </Link>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {selectedEntry.resolution_status ? (
                    <div className="tooltip" data-tip="">
                      <span
                        className={`badge font-mono text-xs shadow-md ${
                          selectedEntry.resolution_status === "resolved"
                            ? "badge-info"
                            : "badge-accent"
                        }`}
                      >
                        {selectedEntry.resolution_status === "resolved"
                          ? "Resolved"
                          : "Declined"}
                      </span>
                      <div className="tooltip-content ml-5 rounded border border-base-300 bg-base-100 p-2 text-base-content shadow-lg">
                        <div className="flex flex-col items-start gap-1 text-xs">
                          <span>
                            {getUserDisplayName(
                              selectedEntry.resolved_by,
                            )}
                          </span>
                          <time className="opacity-70">
                            {selectedEntry.resolved_at
                              ? new Date(
                                  selectedEntry.resolved_at,
                                ).toLocaleString("sv-SE")
                              : ""}
                          </time>
                          {selectedEntry.resolution_status ===
                            "declined" &&
                            selectedEntry.resolution_reason && (
                              <p className="mt-1 italic">
                                Reason: {selectedEntry.resolution_reason}
                              </p>
                            )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`badge font-mono text-xs shadow-md ${
                        selectedEntry.update_needed === null
                          ? "badge-ghost"
                          : selectedEntry.update_needed
                            ? "badge-error"
                            : "badge-success"
                      }`}
                    >
                      {selectedEntry.update_needed === null
                        ? "N/A"
                        : selectedEntry.update_needed
                          ? "Updates needed"
                          : "No updates needed"}
                    </div>
                  )}

                  {selectedEntry.relevance_score != null && (
                    <div className="badge font-mono text-xs text-accent-content shadow-md badge-accent">
                      Relevance:{" "}
                      {selectedEntry.relevance_score.toFixed(2)}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="mb-2 font-semibold">Reasoning:</h4>
                  <p className="mb-3 text-sm">
                    {selectedEntry.reasoning || "N/A"}
                  </p>
                </div>

                {selectedEntry.suggestions?.general_suggestions &&
                  selectedEntry.suggestions.general_suggestions.length >
                    0 && (
                    <div className="mb-3">
                      <h4 className="mb-2 font-semibold">
                        General Suggestions:
                      </h4>
                      <ul className="list-inside list-disc pl-2 text-sm">
                        {selectedEntry.suggestions.general_suggestions.map(
                          (s: string, i: number) => (
                            <li key={i}>
                              {selectedEntry.update_needed ? (
                                s
                              ) : (
                                <del>{s}</del>
                              )}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}

                {selectedEntry.suggestions?.detailed_suggestions &&
                  selectedEntry.suggestions.detailed_suggestions.length >
                    0 && (
                    <div>
                      <h4 className="mt-4 mb-4 font-semibold">
                        Detailed Suggestions:
                      </h4>
                      {selectedEntry.suggestions.detailed_suggestions.map(
                        (suggestion, i, arr) => (
                          <React.Fragment key={i}>
                            <DetailedSuggestionDiff
                              suggestion={suggestion}
                              articleId={articleId}
                              apiBaseUrl={apiBaseUrl}
                              fetchData={fetchData}
                            />
                            {i < arr.length - 1 && (
                              <div className="divider"></div>
                            )}
                          </React.Fragment>
                        ),
                      )}
                    </div>
                  )}

                {selectedEntry.update_needed &&
                  (!selectedEntry.suggestions?.detailed_suggestions ||
                    selectedEntry.suggestions.detailed_suggestions
                      .length === 0) && (
                    <div className="mt-6 p-4 rounded-md border border-base-300 bg-base-200/30">
                       <h4 className="mb-2 font-semibold">Resolve Item:</h4>
                      {generalResolutionError && (
                        <div
                          role="alert"
                          className="mb-3 alert alert-error p-2 text-xs"
                        >
                          {generalResolutionError}
                        </div>
                      )}

                      {!selectedEntry.resolution_status && (
                        <>
                          {showGeneralDeclineReasonInput && (
                            <div className="mb-3">
                              <textarea
                                className="textarea-bordered textarea w-full textarea-sm"
                                placeholder="Why are you dismissing this item? (required)"
                                value={generalDeclineReason}
                                onChange={(e) => {
                                  setGeneralDeclineReason(e.target.value);
                                  if (e.target.value.trim())
                                    setGeneralResolutionError(null);
                                }}
                                rows={2}
                              />
                              <button
                                className="btn mt-1 btn-xs btn-warning"
                                onClick={handleDeclineGeneral}
                                disabled={
                                  isSubmittingGeneralResolution ||
                                  !generalDeclineReason.trim()
                                }
                              >
                                {isSubmittingGeneralResolution ? (
                                  <span className="loading loading-xs loading-spinner"></span>
                                ) : (
                                  "Confirm Dismissal"
                                )}
                              </button>
                            </div>
                          )}

                          {!showGeneralDeclineReasonInput && <div className="flex justify-start gap-2"> {/* Hide buttons if reason input is shown */}
                            <button
                              className="btn text-success-content btn-sm btn-success"
                              onClick={handleResolveGeneral}
                              disabled={isSubmittingGeneralResolution}
                            >
                              {isSubmittingGeneralResolution && !showGeneralDeclineReasonInput ? ( // Show spinner only on this button if it's the one clicked
                                <span className="loading loading-xs loading-spinner"></span>
                              ) : (
                                "Mark as Addressed"
                              )}
                            </button>
                            <button
                              className="btn text-warning-content btn-sm btn-warning"
                              onClick={handleDeclineGeneral} // First click shows reason input
                              disabled={isSubmittingGeneralResolution}
                            >
                               Dismiss Item
                            </button>
                          </div>}
                        </>
                      )}
                    </div>
                  )}
              </div>

              {/* Right Column - Comments */}
              <div className="md:col-span-1 lg:col-span-1 space-y-6 border-l border-base-300 pl-4 md:pl-6">
                <div>
                  <h3 className="mb-4 text-lg font-semibold">
                    Item Comments
                  </h3>
                  {releaseItemCommentError && <div className="alert alert-error text-xs p-2 mb-2">{releaseItemCommentError}</div>}
                  <div className="pr-2 max-h-[400px] overflow-y-auto mb-4"> {/* Scrollable comments */}
                    {(() => {
                      const comments = selectedEntry.comments; // Already sorted in parent or should be sorted here
                      if (comments && comments.length > 0) {
                        // Sort again here if necessary, assuming parent might not always sort
                        const sortedComments = [...comments].sort((a,b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                        return (
                          <ul className="timeline timeline-vertical timeline-compact">
                            {sortedComments.map(
                                (
                                  comment: PersistentNote,
                                  index: number,
                                ) => (
                                  <li key={`comment-${index}-${new Date(comment.timestamp).getTime()}`}> {/* More robust key without relying on optional _id */}
                                    {index !== 0 && (
                                      <hr className="my-1 bg-base-300/50" />
                                    )}
                                    <div className="timeline-middle px-1">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        className="h-4 w-4 text-secondary"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                    <div className="timeline-end p-2 text-sm"> {/* Changed to timeline-end for standard layout */}
                                      <p className="mb-1 break-words whitespace-normal">
                                        {comment.text}
                                      </p>
                                      <div className="flex flex-wrap items-center justify-start gap-x-1 gap-y-0.5 text-xs text-base-content opacity-80">
                                        {comment.user && (
                                          <div
                                            className={`avatar-xs avatar ${
                                              !comment.user.avatar_url
                                                ? "placeholder"
                                                : ""
                                            }`}
                                          >
                                            <div className="h-4 w-4 rounded-full bg-neutral text-neutral-content">
                                              {comment.user.avatar_url ? (
                                                <img
                                                  src={constructAvatarUrl(
                                                    apiBaseUrl,
                                                    comment.user
                                                      .avatar_url,
                                                  )}
                                                  alt={getUserDisplayName(
                                                    comment.user,
                                                  )}
                                                />
                                              ) : (
                                                <span>
                                                  {getAvatarFallback(
                                                    comment.user,
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        <span>
                                          {getUserDisplayName(
                                            comment.user,
                                          )}
                                        </span>
                                        <time className="whitespace-nowrap">
                                          {new Date(
                                            comment.timestamp,
                                          ).toLocaleString("sv-SE", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </time>
                                      </div>
                                    </div>
                                  </li>
                                ),
                              )}
                          </ul>
                        );
                      } else {
                        return (
                          <p className="text-sm text-base-content opacity-60">
                            No comments for this item yet.
                          </p>
                        );
                      }
                    })()}
                  </div>
                  <div className="mt-6 space-y-2">
                    <textarea
                      className="textarea w-full textarea-bordered textarea-ghost focus:outline-none" // Added textarea-bordered
                      placeholder="Add a comment for this item..."
                      value={releaseItemCommentInput}
                      onChange={handleReleaseItemCommentChange}
                      rows={3}
                    />
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() =>
                        handleAddReleaseItemComment(
                          selectedEntry.release_note_id,
                          selectedEntry.release_item_title,
                        )
                      }
                      disabled={
                        isSubmittingReleaseItemComment ||
                        !releaseItemCommentInput.trim()
                      }
                    >
                      {isSubmittingReleaseItemComment && (
                        <span className="loading loading-xs loading-spinner"></span>
                      )}
                      {isSubmittingReleaseItemComment
                        ? "Adding..."
                        : "Add Comment"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisEntryDetailDrawer; 